import type { ReconciledPiece } from '../../api/types';

/**
 * ⚠ **TWO DIFFERENT DOCUMENTS ARE NAMED IN THIS FILE, and both are called an "invoice".** D97
 * renamed one of them and not the other, so read the list below before changing any identifier
 * here. Nothing in the type checker or the test suite can tell them apart: moving an identifier
 * from one column to the other compiles, passes, and bills a retailer for goods they do not own.
 *
 *   THE DESPATCH DOCUMENT — the **consignment invoice**. Goods placed with the retailer, title
 *   retained by Miraki (SH-06); nothing on it is payable but the courier charge (SH-13). It is
 *   what this module's data arrives on, and R-07/R-26 render it. Named here by:
 *     · the route `/shop/consignment-invoices/:id/reconciliation` (renamed by D97)
 *
 *   THE SALE DOCUMENT — the **purchase invoice**, raised over a piece the retailer has reported sold.
 *   This is the one that asks for money. D97 release 1 does NOT rename it; it is still the sale
 *   invoice everywhere. Named here by:
 *     · `TallyAction`'s `VIEW_INVOICE` member
 *     · `ReconciledPiece.invoiceReference` / `.invoiceState` (fields on the wire)
 *     · `PAYABLES_INVOICE_PARAM`, `payablesLinkFor`, `invoiceAsked` — the link to R-22 Payables
 *
 * W4 and W5 — the two actions on R-07's delivery tally, kept out of the component so the rule
 * "which control appears, on which piece, for whom" can be tested without a browser.
 *
 * Everything this file reasons about arrives from
 * `GET /shop/consignment-invoices/:id/reconciliation`.
 * It computes no figure: the projection already owns the buckets, the per-piece value and the
 * totals, and `reconciliation.ts` states why (money from the purchase-invoice tables, physical state
 * from `piece.status`, never money from the status).
 *
 * **The response is already composed to the role.** A Buyer is sent no `invoiceReference` key at
 * all, and Accounts no `pieceId`; both are omissions at the source, not fields this screen
 * declines to render. So the rules below test for a field's PRESENCE as well as for the
 * permission — the two agree today, and if they ever stop agreeing the missing field is the
 * safer answer.
 */

/**
 * `REPORT_SALE` — SL-01, against the route My Inventory already calls. `VIEW_INVOICE` — a link
 * to the PURCHASE invoice on R-22, and **never a Pay button**: under FN-06 the retailer pays by bank
 * transfer and Admin confirms it when it arrives, so a control promising payment here is the class
 * of defect this screen was built to remove.
 */
export type TallyAction = 'REPORT_SALE' | 'VIEW_INVOICE';

/**
 * Which actions a piece gets, for this caller. DB-07's rule is that a control the server would
 * refuse is never rendered, so this returns the empty list rather than something to disable.
 *
 *   REPORT_SALE   the piece is in the `held` bucket, the caller holds `shop.report_sale`, and the
 *                 response carried the piece's id — which only an inventory audience receives,
 *                 and which `zMarkSold` needs to name the piece.
 *   VIEW_INVOICE  a PURCHASE invoice has been raised over the piece (FN-04) and the caller holds
 *                 `shop.ledger`. Keyed on the reference, not on the bucket: a settled piece still
 *                 has a purchase invoice to read, and `invoiceReference` is absent for a Buyer and
 *                 `null` for a piece nobody has invoiced yet — neither is a link.
 */
export function actionsFor(piece: ReconciledPiece, permissions: readonly string[]): TallyAction[] {
  const out: TallyAction[] = [];
  if (
    piece.bucket === 'held'
    && piece.pieceId !== undefined
    && permissions.includes('shop.report_sale')
  ) out.push('REPORT_SALE');
  if (
    piece.invoiceReference !== undefined && piece.invoiceReference !== null
    && permissions.includes('shop.ledger')
  ) out.push('VIEW_INVOICE');
  return out;
}

export const PAYABLES_HOME = '/shop/payables';

/** The query parameter R-22 reads to open on one PURCHASE invoice. Named here so both sides agree. */
export const PAYABLES_INVOICE_PARAM = 'invoice';

/**
 * Where "View invoice" goes: R-22 Payables, with the PURCHASE invoice named so the screen opens on its
 * invoices tab and marks the row, rather than landing the retailer near it.
 *
 * The **reference** carries it, not the id: it is what a person reads, what R-22's table shows,
 * and the only identifier the tally is given for a purchase invoice.
 */
export const payablesLinkFor = (reference: string): string =>
  `${PAYABLES_HOME}?${PAYABLES_INVOICE_PARAM}=${encodeURIComponent(reference)}`;

/** R-22's side of the same link: the purchase invoice a URL asks to be opened on, or null. */
export function invoiceAsked(search: string): string | null {
  const asked = new URLSearchParams(search).get(PAYABLES_INVOICE_PARAM);
  return asked && asked.trim() !== '' ? asked : null;
}
